import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  ArrowRight,
  CreditCard,
  BadgePercent,
  Ticket,
  ShieldCheck,
  Sparkles,
  X,
  AlertCircle,
} from 'lucide-react';
import { useApiMutation } from '../../hooks/useMutation';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authorizedEmails } from '../../utils/Constant';

type PartnerStatusResponse = {
  success: boolean;
  message: string;
  data: {
    active: boolean;
    plan: string;
    whiteLabelEnabled: boolean;
    branding: {
      primaryColor: string;
      logoUrl: string | null;
    };
    features: {
      smsEnabled: boolean;
      automationEnabled: boolean;
      exportsEnabled: boolean;
    };
  };
};

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  limits: string[];
  buttonText: string;
  buttonVariant: string;
  isCurrent?: boolean;
  isPopular?: boolean;
  stripePriceId?: string;
}

type ChangePlanResponse = {
  success: boolean;
  message: string;
  data: {
    message?: string;
    checkoutUrl: string | null;
    reference?: string | null;
    effectiveDate?: string | null;
    remainingDays?: number | null;
  };
};

type VerifyCouponResponse = {
  success: boolean;
  message: string;
  data?: {
    verified?: boolean;
  };
};

const PartnerUpgrade: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
 
  
  const [selectedPlan, setSelectedPlan] = useState<PricingTier | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponDecision, setCouponDecision] = useState<'idle' | 'with_coupon' | 'without_coupon'>('idle');
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponVerified, setCouponVerified] = useState(false);
  const [showProceedWithoutCoupon, setShowProceedWithoutCoupon] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [isUpgrade, setIsUpgrade] = useState(false);

  // Fetch current partner status
  const { data: partnerStatus, isLoading: statusLoading } = useApiQuery<PartnerStatusResponse>({
    queryKey: ['partner', 'status'],
    request: {
      method: 'GET',
      url: '/partner'
    }
  });

  const currentPlan = partnerStatus?.data?.plan?.toLowerCase();

  // Plan hierarchy for determining upgrade vs downgrade
  const getPlanLevel = (planName: string): number => {
    const planLevels: { [key: string]: number } = {
      'free': 1,
      'community': 2,
      'field_pro': 3,
      'partner_growth': 4,
      'campaign': 5,
      'FREE': 1,
      'COMMUNITY': 2,
      'FIELD_PRO': 3,
      'PARTNER_GROWTH': 4,
      'CAMPAIGN': 5
    };
    return planLevels[planName] || 0;
  };

  const resetCouponFlow = () => {
    setCouponDecision('idle');
    setCouponCode('');
    setCouponError('');
    setCouponVerified(false);
    setShowProceedWithoutCoupon(false);
  };

  const resetAllModals = () => {
    resetCouponFlow();
    setShowCouponModal(false);
    setShowUpgradeModal(false);
    setShowDowngradeModal(false);
    setSelectedPlan(null);
    setIsLoading(false);
  };

  const closeCouponModal = () => {
    setShowCouponModal(false);
    resetCouponFlow();
    setSelectedPlan(null);
    setIsLoading(false);
  };

  const performPlanChange = (tier: PricingTier, couponId?: string) => {
    setSelectedPlan(tier);
    setIsLoading(true);

    changePlanMutation.mutate({
      url: '/partner/plan/change-plan',
      method: 'POST',
      data: {
        plan: tier.name.toUpperCase().replace(/\s+/g, '_'),
        ...(couponId ? { couponId } : {})
      }
    });
  };

  const changePlanMutation = useApiMutation<ChangePlanResponse>({
    onSuccess: (response: ChangePlanResponse) => {
      setIsLoading(false);
      resetAllModals();
      
      if (response.success && response.data) {
        // Handle downgrade response (effective date and remaining days present)
        if (response.data.effectiveDate && response.data.remainingDays !== null) {
          toast.success(`Downgrade scheduled successfully. It will take effect on ${new Date(response.data.effectiveDate).toLocaleDateString()} (${response.data.remainingDays} days remaining).`);
          // Navigate to dashboard after successful downgrade scheduling
          setTimeout(() => {
            navigate('/partner/dashboard');
          }, 2000);
        }
        // Handle upgrade response (reference present, no effective date)
        else if (response.data.reference) {
          if (response.data.checkoutUrl) {
            toast.success('Proceeding with payment...');
            window.location.href = response.data.checkoutUrl;
          } else {
            toast.success('Upgrade applied successfully! Stripe will charge the prorated difference immediately.');
            // Navigate to dashboard after successful upgrade
            setTimeout(() => {
              navigate('/partner/dashboard');
            }, 2000);
          }
        }
        // Handle free plan or other scenarios
        else if (response.data.checkoutUrl === null && response.data.message) {
          toast.success(response.data.message);
          // Navigate to dashboard after successful plan change
          setTimeout(() => {
            navigate('/partner/dashboard');
          }, 2000);
        } else if (response.data.checkoutUrl) {
          toast.success('Proceeding with payment...');
          window.location.href = response.data.checkoutUrl;
        }
      }
    },
    onError: (error) => {
      console.error('Error changing plan:', error);
      setIsLoading(false);
      toast.error('Failed to change plan. Please try again.');
    }
  });

  const verifyCouponMutation = useApiMutation<VerifyCouponResponse>({
    onSuccess: (response) => {
      const isVerified = Boolean(response?.success && response?.data?.verified);

      if (!isVerified) {
        const message = 'Coupon is invalid, enter another.';
        setIsLoading(false);
        setCouponVerified(false);
        setCouponError(message);
        setShowProceedWithoutCoupon(true);
        toast.error(message);
        return;
      }

      if (!selectedPlan) {
        setIsLoading(false);
        toast.error('Select a plan before applying a coupon.');
        return;
      }

      const trimmedCoupon = couponCode.trim();

      setCouponVerified(true);
      setCouponError('');
      setShowProceedWithoutCoupon(false);
      setShowCouponModal(false);
      toast.success('Coupon verified. Redirecting to checkout...');
      performPlanChange(selectedPlan, trimmedCoupon);
    },
    onError: () => {
      const message = 'Coupon is invalid, enter another.';
      setCouponVerified(false);
      setCouponError(message);
      setShowProceedWithoutCoupon(true);
      setIsLoading(false);
      toast.error(message);
    }
  });

  const pricingTiers: PricingTier[] = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Pilots, churches, community orgs',
      features: [
        'Registration self-check embed',
        'Registration redirect flow',
        'Usage based messaging',
        'Basic registration check activity export only',
        'Limited level API calls'
      ],
      limits: ['No canvasser seats', 'No reminder Opt-ins', 'no advanced exports', 'no engagement logging', 'No shift tracking', '<1000 redirect checks'],
      buttonText: 'Select Freemium',
      buttonVariant: 'secondary',
      isCurrent: currentPlan === 'free',
      stripePriceId: 'price_free_tier'
    },
    {
      name: 'Community',
      price: '$99',
      period: '/month',
      description: 'C3 nonprofits running programs',
      features: [
        'Registration self-check embed',
        'Registration redirect flow',
        'Polling info & Voting method explanation',
        'Usage based messaging',
        'Polling location lookup',
        'Moderate level API calls'
      ],
      limits: ['Limited canvasser seats <5', 'no audit/compliance exports', 'no field summaries', '<5,000 redirect checks', '<1000 Reminder opt-ins'],
      buttonText: currentPlan === 'community' ? 'Current Plan' : 'Select Plan',
      buttonVariant: currentPlan === 'community' ? 'secondary' : 'primary',
      isCurrent: currentPlan === 'community',
      stripePriceId: 'price_community_tier'
    },
    {
      name: 'Field Pro',
      price: '$499',
      period: '/month',
      description: 'Core field operations',
      features: [
        'Registration self-check embed',
        'Start / end shift',
        'Registration redirect flow',
        'Usage based messaging',
        'Reminder opt-in',
        'Full canvasser workflows',
        'partner admin canvass mode',
        'Registration + field activity summaries',
        'Higher level API calls'
      ],
      limits: ['Limited canvasser seats < 25','No automated broadcasts', 'no compliance/audit exports', '<25,000 redirect checks', '<5,000 Reminder opt-ins'],
      buttonText: currentPlan === 'field_pro' ? 'Current Plan' : 'Select Plan',
      buttonVariant: currentPlan === 'field_pro' ? 'secondary' : 'primary',
      isCurrent: currentPlan === 'field_pro',
      isPopular: true,
      stripePriceId: 'price_field_pro_tier'
    },
    {
      name: 'Partner Growth',
      price: '$1250',
      period: '/month',
      description: 'Scaled organizations',
      features: [
        'Everything in Field Pro, plus:',
        'Registration redirect flow',
        'All partner-scoped inclusive compliance',
        'Usage-based messaging (broadcasts enabled)',
        'Export permissions',
        'High level API calls'
      ],
      limits: ['Limited canvasser seats < 50','Higher API limits required', '<15,000 Reminder opt-ins', 'admin approvals for exports', '<100,000 redirect checks'],
      buttonText: currentPlan === 'partner_growth' ? 'Current Plan' : 'Select Plan',
      buttonVariant: currentPlan === 'partner_growth' ? 'secondary' : 'primary',
      isCurrent: currentPlan === 'partner_growth',
      stripePriceId: 'price_partner_growth_tier'
    },
    {
      name: 'Campaign',
      price: '$2500',
      period: '/month',
      description: 'PAC/Party organizations',
      features: [
        'Everything in Partner Growth',
        'Hard tenant isolation',
        'PAC/Party compliance firewall',
        'Additional logging',
        'Strict export controls',
        'FEC compliance ready',
        'Dedicated compliance support',
        'Enterprise level API calls'
      ],
      limits: ['Limited canvasser seats < 100',
        'Custom redirect check volume and schemas',
        'Stricter approvals', 'compliance controls', '<150,000 redirect checks', '<25,000 Reminder opt-ins'
      ],
      buttonText: currentPlan === 'campaign' ? 'Current Plan' : 'Select plan',
      buttonVariant: currentPlan === 'campaign' ? 'secondary' : 'primary',
      isCurrent: currentPlan === 'campaign',
      stripePriceId: 'price_campaign_tier'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const handlePlanSelect = (tier: PricingTier) => {
    if (tier.isCurrent) return;

    if (tier.name.toLowerCase() === 'free') {
      performPlanChange(tier);
      return;
    }

    setSelectedPlan(tier);
    
    // Determine if this is an upgrade or downgrade
    const currentLevel = getPlanLevel(currentPlan || '');
    // Convert tier name to API format for comparison
    const tierPlanName = tier.name.toUpperCase().replace(/\s+/g, '_');
    const selectedLevel = getPlanLevel(tierPlanName);
    const upgrade = selectedLevel > currentLevel;
    setIsUpgrade(upgrade);
    
    // Check if user email is authorized for coupon functionality
    const normalizedEmail = user?.email?.toLowerCase();
    const isAuthorizedEmail = Boolean(
      normalizedEmail && authorizedEmails.some((email) => email.toLowerCase() === normalizedEmail)
    );
    
    if (isAuthorizedEmail) {
      setShowCouponModal(true);
      resetCouponFlow();
    } else if (upgrade) {
      setShowUpgradeModal(true);
    } else {
      setShowDowngradeModal(true);
    }
  };

  const handleCouponVerify = () => {
    const trimmedCoupon = couponCode.trim();

    if (!trimmedCoupon) {
      setCouponError('Enter your coupon code to continue.');
      return;
    }

   

    setCouponError('');
    setCouponVerified(false);
    setShowProceedWithoutCoupon(false);
    setIsLoading(true);

    verifyCouponMutation.mutate({
      url: `/partner/discount/verify-coupon?couponId=${encodeURIComponent(trimmedCoupon)}`,
      method: 'GET'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-10 px-4 sm:px-6 lg:px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Upgrade Your Plan
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Select the perfect plan for your organization's needs
          </p>

          <motion.div
            className="inline-flex items-center justify-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Check className="w-4 h-4 mr-2" />
            Compliance-Safe Billing
          </motion.div>
        </motion.div>

        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <span className="text-sm text-gray-500">Current Plan:</span>
          <span className="ml-2 text-sm font-semibold text-gray-900">
            {statusLoading ? 'Loading...' : (partnerStatus?.data?.plan || 'Unknown')}
          </span>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6 mb-14"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              variants={cardVariants}
              whileHover="hover"
              className={`relative bg-white rounded-2xl shadow-lg border-2 ${
                tier.isCurrent
                  ? 'border-green-500 ring-4 ring-green-100 bg-green-50'
                  : tier.isPopular
                    ? 'border-blue-500 ring-4 ring-blue-100'
                    : 'border-gray-200'
                } overflow-hidden`}
            >
              {tier.isCurrent && (
                <motion.div
                  className="absolute -top-2 left-[30%] transform -translate-x-1/2 z-[10000]"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                >
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-1 rounded-full text-xs font-medium flex items-center">
                    Current Plan
                  </div>
                </motion.div>
              )}
              {tier.isPopular && !tier.isCurrent && (
                <motion.div
                  className="absolute -top-2 left-[30%] transform -translate-x-1/2 z-[1000]"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                >
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-1 rounded-full text-xs font-medium flex items-center">
                    Most Popular
                  </div>
                </motion.div>
              )}

              <div className="p-6 h-full flex flex-col">
                {/* Tier Name */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {tier.name}
                </h3>

                {/* Price */}
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-lg text-gray-600 ml-1">{tier.period}</span>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6 text-sm">
                  {tier.description}
                </p>

                {/* Features */}
                <div className="flex-grow mb-6">
                  <ul className="space-y-3">
                    {tier.features.map((feature, featureIndex) => (
                      <motion.li
                        key={featureIndex}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + index * 0.1 + featureIndex * 0.05 }}
                      >
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* Limits */}
                  {tier.limits.length > 0 && (
                    <div className="mt-2 pt-2 -ml-3 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Limits:</h4>
                      <ul className="space-y-2">
                        {tier.limits.map((limit, limitIndex) => (
                          <motion.li
                            key={limitIndex}
                            className="flex items-start"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.7 + index * 0.1 + limitIndex * 0.05 }}
                          >
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0" />
                            <span className="text-gray-600 text-xs">{limit}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Button */}
                <motion.button
                  onClick={() => handlePlanSelect(tier)}
                  className={`w-full py-3 px-6 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    tier.isCurrent
                      ? 'bg-green-600 text-white cursor-not-allowed'
                      : tier.isPopular
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  disabled={tier.isCurrent || isLoading}
                  whileHover={!tier.isCurrent && !isLoading ? { scale: 1.05 } : {}}
                  whileTap={!tier.isCurrent && !isLoading ? { scale: 0.95 } : {}}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.3 }}
                >
                  {isLoading && selectedPlan?.name === tier.name ? (
                    <div className="flex  justify-center gap-2 items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </div>
                  ) : (
                    <>
                      {tier.buttonText}
                      {!tier.isCurrent && <ArrowRight className="w-4 h-4" />}
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Usage-Based Billing Model Section */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Usage-Based Billing Model</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* What's Billed */}
            <div className='bg-red-100 border-red-500 border rounded-md p-5'>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Billed</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">$0.002 per redirect check over included volume (voter registration verification)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">$0.005 per opt-in over included volume (SMS reminders sent)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600"> Usage-based, billed separately via third-party providers (Messaging delivery (SMS/email))</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Tier-locked exports access (generation + approval required for advanced tiers)
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Canvasser accounts (if applicable)</span>
                </li>
              </ul>
            </div>

            {/* Never Billed On */}
            <div className='bg-lime-100 border-lime-500 border rounded-md p-5'>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Never Billed On</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Voter data storage</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Number of voters reached</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Basic analytics and reporting</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="text-center text-gray-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <p>All plans include our core compliance features and 24/7 support</p>
        </motion.div>
      </div>

      {showCouponModal && selectedPlan && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 px-4 py-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full top-5 max-w-4xl h-[90vh]  z-[9999999] overflow-y-auto rounded-[32px] border border-slate-200 bg-white shadow-2xl"
          >
            <button
              type="button"
              onClick={closeCouponModal}
              className="absolute right-5 top-5 rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
              aria-label="Close coupon modal"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="bg-gradient-to-r from-[#082f49] via-[#155e75] to-[#0f766e] px-8 py-8 text-white">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur-sm">
                <Sparkles className="h-4 w-4" />
                Welcome to Beta testing
              </div>
              <h2 className="max-w-2xl text-2xl font-bold leading-tight">
                Kindly enter your coupon code to get a discounted upgrade.
              </h2>
             
            </div>

            <div className="grid gap-6 bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.08),_transparent_45%),linear-gradient(180deg,#f8fafc_0%,#ffffff_100%)] px-8 py-8 lg:grid-cols-[1.1fr,0.9fr]">
              <div className="space-y-5">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Selected plan</p>
                      <h3 className="mt-2 text-2xl font-bold text-slate-900">{selectedPlan.name}</h3>
                      <p className="mt-1 text-sm text-slate-600">{selectedPlan.description}</p>
                    </div>
                    <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-right">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Monthly total</p>
                      <p className="mt-1 text-2xl font-bold text-emerald-900">
                        {selectedPlan.price}
                        <span className="text-sm font-medium text-emerald-800">{selectedPlan.period}</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCouponDecision('with_coupon');
                      setCouponError('');
                      setCouponVerified(false);
                    }}
                    className={`rounded-3xl flex items-center gap-2 border p-4 text-left transition ${
                      couponDecision === 'with_coupon'
                        ? 'border-cyan-500 bg-cyan-50 shadow-md shadow-cyan-100'
                        : 'border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/40'
                    }`}
                  >
                    <div className="mb-4 inline-flex rounded-2xl bg-cyan-100 p-2 text-cyan-700">
                      <BadgePercent className="h-6 w-6" />
                    </div>
                    <h4 className="text-md font-semibold text-slate-900">Yes, I have a coupon</h4>
                    
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setCouponDecision('without_coupon');
                      setCouponError('');
                      setCouponVerified(false);
                      setShowProceedWithoutCoupon(false);
                    }}
                    className={`rounded-3xl flex items-center gap-2 border p-4 text-left transition ${
                      couponDecision === 'without_coupon'
                        ? 'border-amber-500 bg-amber-50 shadow-md shadow-amber-100'
                        : 'border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/40'
                    }`}
                  >
                    <div className="mb-4 inline-flex rounded-2xl bg-amber-100 p-2 text-amber-700">
                      <CreditCard className="h-6 w-6" />
                    </div>
                    <h4 className="text-md font-semibold text-slate-900">No coupon, continue</h4>
                    
                  </button>
                </div>

                {couponDecision === 'with_coupon' && (
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="rounded-2xl bg-slate-900 p-3 text-white">
                        <Ticket className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-slate-900">Enter your coupon code</h4>
                        <p className="text-sm text-slate-600">Only letters and numbers are allowed.</p>
                      </div>
                    </div>

                    <label htmlFor="coupon-code" className="mb-2 block text-sm font-semibold text-slate-700">
                      Coupon code
                    </label>
                    <input
                      id="coupon-code"
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        const nextValue = e.target.value.replace(/[^a-z0-9_\-\s]/gi, '');
                        setCouponCode(nextValue);
                        if (couponError) {
                          setCouponError('');
                        }
                        if (couponVerified) {
                          setCouponVerified(false);
                        }
                      }}
                      placeholder="Enter alphanumerical coupon code"
                      className={`w-full rounded-2xl border bg-slate-50 px-4 py-3 text-base text-slate-900 outline-none transition ${
                        couponError
                          ? 'border-red-400 focus:border-red-500'
                          : couponVerified
                            ? 'border-emerald-400 focus:border-emerald-500'
                            : 'border-slate-300 focus:border-cyan-500'
                      }`}
                    />

                    {couponError && (
                      <p className="mt-2 flex items-center gap-2 text-sm font-medium text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {couponError}
                      </p>
                    )}

                    {couponVerified && (
                      <p className="mt-2 flex items-center gap-2 text-sm font-medium text-emerald-700">
                        <ShieldCheck className="h-4 w-4" />
                        Coupon verified successfully. Preparing your upgrade.
                      </p>
                    )}

                    <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={handleCouponVerify}
                        disabled={isLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isLoading ? (
                          <>
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Verifying coupon...
                          </>
                        ) : (
                          <>
                            Verify and proceed
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>

                      {showProceedWithoutCoupon && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowCouponModal(false);
                            setShowUpgradeModal(true);
                          }}
                          disabled={isLoading}
                          className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Proceed without coupon
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {couponDecision === 'without_coupon' && (
                  <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
                    <p className="text-sm text-amber-900">
                      You chose to continue without a discount code. When you proceed, the current upgrade flow will take you into payment for the <span className="font-semibold">{selectedPlan.name}</span> plan.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCouponModal(false);
                        setShowUpgradeModal(true);
                      }}
                      disabled={isLoading}
                      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 px-6 py-3 font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isLoading ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Preparing payment...
                        </>
                      ) : (
                        <>
                          Continue to payment
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">How this works</h4>
                  <div className="mt-5 space-y-4">
                    <div className="flex gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">1</div>
                      <div>
                        <p className="font-semibold text-slate-900">Select your upgrade path</p>
                        <p className="text-xs text-slate-600">Choose whether you want to apply a discount coupon first or continue without one.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">2</div>
                      <div>
                        <p className="font-semibold text-slate-900">Verify coupon instantly</p>
                        <p className="text-xs text-slate-600">If the code is valid, the upgrade continues automatically. If not, you will see an inline error and a toast.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">3</div>
                      <div>
                        <p className="font-semibold text-slate-900">Proceed to upgrade and payment</p>
                        <p className="text-xs text-slate-600">Your card details are securely saved and processed through our payment provider.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Upgrade Confirmation Modal */}
      {showUpgradeModal && selectedPlan && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 px-4 py-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-2xl z-[9999999] overflow-y-auto rounded-[32px] border border-slate-200 bg-white shadow-2xl"
          >
            <button
              type="button"
              onClick={() => resetAllModals()}
              className="absolute right-5 top-5 rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
              aria-label="Close upgrade modal"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-8 text-white">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur-sm">
                <Sparkles className="h-4 w-4" />
                Plan Upgrade
              </div>
              <h2 className="text-2xl font-bold leading-tight">
                Upgrade to {selectedPlan.name}
              </h2>
            </div>

            <div className="px-8 py-8">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Upgrading to</p>
                    <h3 className="mt-2 text-2xl font-bold text-slate-900">{selectedPlan.name}</h3>
                    <p className="mt-1 text-sm text-slate-600">{selectedPlan.description}</p>
                  </div>
                  <div className="rounded-2xl bg-blue-50 px-4 py-3 text-right">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Monthly total</p>
                    <p className="mt-1 text-2xl font-bold text-blue-900">
                      {selectedPlan.price}
                      <span className="text-sm font-medium text-blue-800">{selectedPlan.period}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 mb-6">
                <h4 className="text-sm font-semibold text-amber-900 mb-3">Important Information</h4>
                <ul className="space-y-2 text-sm text-amber-800">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-600 rounded-full mt-2 flex-shrink-0" />
                    <span>Stripe will charge the prorated difference immediately</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-600 rounded-full mt-2 flex-shrink-0" />
                    <span>Your new features will be available right away</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-600 rounded-full mt-2 flex-shrink-0" />
                    <span>You'll receive a confirmation email with transaction details</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => resetAllModals()}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (couponVerified && couponCode.trim()) {
                      performPlanChange(selectedPlan, couponCode.trim());
                    } else {
                      performPlanChange(selectedPlan);
                    }
                  }}
                  disabled={isLoading}
                  className="flex-1 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-semibold text-white transition hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading ? (
                    <div className="flex justify-center gap-2 items-center">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Processing...
                    </div>
                  ) : (
                    <div className="flex justify-center text-center items-center gap-2">
                      Confirm Upgrade
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Downgrade Confirmation Modal */}
      {showDowngradeModal && selectedPlan && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 px-4 py-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-2xl z-[9999999] overflow-y-auto rounded-[32px] border border-slate-200 bg-white shadow-2xl"
          >
            <button
              type="button"
              onClick={() => resetAllModals()}
              className="absolute right-5 top-5 rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
              aria-label="Close downgrade modal"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="bg-gradient-to-r from-orange-600 to-red-600 px-8 py-8 text-white">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur-sm">
                <AlertCircle className="h-4 w-4" />
                Plan Downgrade
              </div>
              <h2 className="text-2xl font-bold leading-tight">
                Downgrade to {selectedPlan.name}
              </h2>
            </div>

            <div className="px-8 py-8">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Downgrading to</p>
                    <h3 className="mt-2 text-2xl font-bold text-slate-900">{selectedPlan.name}</h3>
                    <p className="mt-1 text-sm text-slate-600">{selectedPlan.description}</p>
                  </div>
                  <div className="rounded-2xl bg-orange-50 px-4 py-3 text-right">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">New monthly total</p>
                    <p className="mt-1 text-2xl font-bold text-orange-900">
                      {selectedPlan.price}
                      <span className="text-sm font-medium text-orange-800">{selectedPlan.period}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-red-200 bg-red-50 p-5 mb-6">
                <h4 className="text-sm font-semibold text-red-900 mb-3">Important Information</h4>
                <ul className="space-y-2 text-sm text-red-800">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                    <span>Downgrade will take effect at the end of your current billing cycle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                    <span>You will continue to have access to current plan features until then</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                    <span><strong>No refunds</strong> will be provided for the current billing period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                    <span>Some features may become unavailable after downgrade takes effect</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => resetAllModals()}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => performPlanChange(selectedPlan)}
                  disabled={isLoading}
                  className="flex-1 text-center rounded-full bg-gradient-to-r from-orange-600 to-red-600 px-6 py-3 font-semibold text-white transition hover:from-orange-700 hover:to-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading ? (
                    <div className="flex  justify-center gap-2 items-center">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Processing...
                    </div>
                  ) : (
                    <div className="flex justify-center text-center items-center gap-2">
                      Confirm Downgrade
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PartnerUpgrade;
